import React, { useState } from 'react';
import LoadingButton from '../common/LoadingButton.jsx';

const LoginModal = ({ show, onClose, loading, onSubmit }) => {
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    username: '', // Used for login - can be email or phone
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    emailNotifications: false,
    phoneNotifications: false,
  });
  const [error, setError] = useState('');

  const resetForm = () => {
    setFormData({
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      emailNotifications: false,
      phoneNotifications: false,
    });
  };

  const handleClose = () => {
    onClose();
    resetForm();
    setAuthMode('login');
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    const result = await onSubmit(authMode, formData);
    if (!result?.success) {
      setError(result?.error || 'Something went wrong. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {authMode === 'login' ? 'Login' : 'Register'}
          </h3>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setAuthMode('login')}
              className={`px-3 py-1 text-sm rounded ${
                authMode === 'login'
                  ? 'bg-white shadow text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setAuthMode('register')}
              className={`px-3 py-1 text-sm rounded ${
                authMode === 'register'
                  ? 'bg-white shadow text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Register
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {authMode === 'register' && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="First Name *"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
              <input
                type="text"
                placeholder="Last Name *"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
          )}

          {authMode === 'login' ? (
            <input
              type="text"
              placeholder="Email or Phone Number *"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          ) : (
            <>
              <input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
              <p className="text-xs text-gray-600">
                * At least one of email or phone number is required
              </p>
            </>
          )}

          <input
            type="password"
            placeholder="Password *"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            onKeyPress={authMode === 'login' ? handleKeyPress : undefined}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          />

          {authMode === 'register' && (
            <>
              <input
                type="password"
                placeholder="Confirm Password *"
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({ ...formData, confirmPassword: e.target.value })
                }
                onKeyPress={handleKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />

              {/* Notification Preferences */}
              <div className="pt-2">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Notification Preferences
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="emailNotifications"
                      checked={formData.emailNotifications}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          emailNotifications: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={loading}
                    />
                    <label
                      htmlFor="emailNotifications"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Email notifications (game invitations & results)
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="phoneNotifications"
                      checked={formData.phoneNotifications}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          phoneNotifications: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={loading}
                    />
                    <label
                      htmlFor="phoneNotifications"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      I consent to receive SMS notifications from Changing 500
                      about game invitations and results. Message frequency may
                      vary. Message and data rates may apply. Reply STOP to opt
                      out.
                      <a
                        href="/terms"
                        target="_blank"
                        className="text-blue-600 underline ml-1"
                      >
                        Terms
                      </a>{' '}
                      |
                      <a
                        href="/privacy"
                        target="_blank"
                        className="text-blue-600 underline ml-1"
                      >
                        Privacy
                      </a>
                    </label>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={loading}
          >
            Cancel
          </button>
          <LoadingButton
            loading={loading}
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {authMode === 'login' ? 'Login' : 'Register'}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
